import React, { useEffect } from 'react';
import { useClient } from 'jsonapi-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AiOutlineTrophy } from 'react-icons/ai';
import getIcon from '@/utils/getIcon';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@/hooks/useNotification';
import { Card, CardBody, CardContent } from '@/components/ui/card';
import {
  ActivityLog,
  ActivityLogHeader,
  ActivityLogHeaderTitle,
  ActivityLogCount,
  ActivityLogItem,
  ActivityLogDateTime,
  ActivityLogIcon,
  ActivityLogTitle,
  ActivityLogTag,
  ActivityLogDescription,
  ActivityLogLink
} from '@/components/custom/activity/activity-log';
import { Badge } from '@/components/ui/badge';

export function CourseMonitoringLogActivity({ useType, courseId }) {
  const { t } = useTranslation();
  moment.locale(t('language.language'));
  const { getNotificationForTimeline, setNotificationsTimeline, pageNumber, updateNotificationsList, notificationsTimeline, hasMore, loading, setPageNumber, meta } = useNotification();
  const client = useClient();

  const handleNotificationRead = async notification => {
    if (!notification.unread) return;

    // Mutação para o backend
    await client.mutate(['notifications', notification.id], { unread: false });

    // Atualização do estado local para feedback imediato
    const updatedNotifications = notificationsTimeline.map(item => (item.id === notification.id ? { ...item, unread: false } : item));
    setNotificationsTimeline(updatedNotifications);
  };

  useEffect(() => {
    getNotificationForTimeline(useType, courseId);
  }, []);

  useEffect(() => {
    if (pageNumber !== 1) {
      updateNotificationsList(useType, courseId);
    }
  }, [pageNumber]);

  return (
    <>
      {loading && <Loader />}
      {!loading && notificationsTimeline.length > 0 && (
        <InfiniteScroll
          dataLength={notificationsTimeline.length}
          next={() => setPageNumber(pageNumber + 1)}
          hasMore={hasMore}
          loader={<Loader />}
        >
          <ActivityLog aria-label="Registro de atividades">
            <ActivityLogHeader>
              <ActivityLogHeaderTitle>{t('dashboard.latestUpdates')}</ActivityLogHeaderTitle>
              <ActivityLogCount>
                <strong>{meta?.unread}</strong> {t('dashboard.unreadNotifications')}
              </ActivityLogCount>
            </ActivityLogHeader>

            {notificationsTimeline.map((notification, index) => (
              <ActivityLogItem
                key={notification.id || index}
                isRead={!notification.unread}
              >
                <ActivityLogIcon>{getIcon(notification.icon || notification['notificable-type'])}</ActivityLogIcon>

                {notification['notificable-type'] === 'LearningSystemUserItem' && (notification['use-type'] === 'timeline' || notification['use-type'] === 'timelineAdmin') && (
                  <ActivityLogIcon
                    variant="trophy"
                    className="text-yellow-500"
                  >
                    <AiOutlineTrophy />
                  </ActivityLogIcon>
                )}

                <ActivityLogDateTime dateTime={moment(notification['created-at']).toISOString()}>{moment(notification['created-at']).fromNow()}</ActivityLogDateTime>

                <ActivityLogTitle>
                  {notification.name}
                  {notification.tag && <ActivityLogTag variant="course">{notification.tag}</ActivityLogTag>}
                  {notification['notificable-type'] === 'LearningSystemUserItem' && notification['use-type'] === 'timeline' && (
                    <Badge
                      variant="success"
                      size="xs"
                    >
                      {t('textsCommon.tagConcluded')}
                    </Badge>
                  )}
                </ActivityLogTitle>

                <ActivityLogDescription>
                  {notification.body &&
                    (notification.body.includes('Seu *f6$>certificado*f6$ já está disponível.') || notification.body.includes('Seu *f6$certificado*f6$ já está disponível.') ? notification.body.split(', Seu')[0] + '.' : notification.body)
                      .split('*f6$')
                      .map((body, i) => {
                        if (body[0] === '>') {
                          return (
                            <ActivityLogLink
                              as={Link}
                              to={notification.url || '/'}
                              onClick={() => handleNotificationRead(notification)}
                              key={i}
                            >
                              {body.substring(1)}
                            </ActivityLogLink>
                          );
                        }
                        return body;
                      })}
                </ActivityLogDescription>
              </ActivityLogItem>
            ))}
          </ActivityLog>
        </InfiniteScroll>
      )}

      {!loading && notificationsTimeline.length === 0 && (
        <Card>
          <CardContent>
            <CardBody>
              <EmptyState
                variant="plain"
                type="register"
                title={t('emptyState.noNewsTitle')}
                text={t('emptyState.noNewsText')}
              />
            </CardBody>
          </CardContent>
        </Card>
      )}
    </>
  );
}
